"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
export type DockPosition = "bottom" | "right";
interface DevToolsContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  dockPosition: DockPosition;
  highlightRegions: boolean;
  setHighlightRegions: (highlightRegions: boolean) => void;
  toggleHighlightRegions: () => void;
  setDockPosition: (position: DockPosition) => void;
  toggleDockPosition: () => void;
}
const DevToolsContext = createContext<DevToolsContextValue | null>(null);
export function DevToolsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dockPosition, setDockPosition] = useState<DockPosition>("bottom");
  const [highlightRegions, setHighlightRegions] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const toggleHighlightRegions = useCallback(() => setHighlightRegions(prev => !prev), []);
  const toggleDockPosition = useCallback(() => setDockPosition(prev => prev === "bottom" ? "right" : "bottom"), []);
  return <DevToolsContext.Provider value={{
    isOpen,
    open,
    close,
    toggle,
    dockPosition,
    highlightRegions,
    setDockPosition,
    setHighlightRegions,
    toggleDockPosition,
    toggleHighlightRegions
  }} data-sentry-element="DevToolsContext.Provider" data-sentry-component="DevToolsProvider" data-sentry-source-file="DevToolsContext.tsx">
      {children}
    </DevToolsContext.Provider>;
}
export function useDevTools(): DevToolsContextValue {
  const context = useContext(DevToolsContext);
  if (!context) {
    throw new Error("useDevTools must be used within a DevToolsProvider");
  }
  return context;
}

/**
 * Hook that returns null if not inside a DevToolsProvider.
 * Useful for components that may or may not be within the provider.
 */
export function useDevToolsOptional(): DevToolsContextValue | null {
  return useContext(DevToolsContext);
}